import React from 'react';

// Import config and utils
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
} from '../../util/types';
import { useIntl } from '../../util/reactIntl';
import { required, nonEmptyArray, validateInteger } from '../../util/validators';
// Import shared components
import { FieldSelect, FieldTextInput, FieldBoolean, FieldMultiSelect, InfoPopup } from '..';
// Import modules from this directory
import {
  FieldConfigKey,
  FieldKeys,
  TalentLevel,
  TalentSignupStep,
  TalentType,
  UserType,
} from '../../util/enums';
import { Field } from 'react-final-form';
import { sortOptionsByKeyIntl, toLowerCase } from '../../util/genericHelpers';
import classNames from 'classnames';
import { US_STATES } from '../../config/configExtras';
import RenderInterests from './RenderInterests';
import RenderBrandIndustries from './RenderBrandIndustries';
import RenderSports from './RenderSports';

import css from './CustomExtendedDataField.module.css';
import DateOfBirthField from '../../containers/AuthenticationPage/SignupForm/DateOfBirthField';
import RenderTalentTypes from './RenderTalentTypes';

const fieldComponents = {
  [FieldKeys.INTEREST]: RenderInterests,
  [FieldKeys.SPORT]: RenderSports,
};

const {
  ATHLETE,
  SCHOOL_OR_TEAM,
  SPORT,
  POSITION,
  HOMETOWN,
  ETHNICITY,
  SHIRT_SIZE,
  REFERRED_BY,
  GENDER,
  COMPANY,
  BIO,
  SCHOOL_NAME,
  INTEREST,
  JOB_TITLE,
  TALENT_TYPE,
  TALENT_LEVEL,
  STATE_OR_TERRITORY,
  USER_TYPE_SCT,
  CONFERENCE,
  CURRENT_TOWN,
  HEIGHT,
  WEIGHT,
  AGENCY_FIELD,
  PRIMARY_INDUSTRY,
  SCHOOL_OR_GROUP_NAME,
  DATE_OF_BIRTH,
} = FieldConfigKey;
const { FINAL, INITIAL } = TalentSignupStep;
const { AGENCY, BRAND, FAN, SCHOOL, TALENT } = UserType;

export const createFilterOptions = options =>
  options.map(o => ({
    value: o.option ? o.option : o.key,
    label: o.label,
  }));

export const createMultiFilterOptions = options =>
  options.map(o => ({
    value: o.option ? o.option : o.key,
    label: o.label,
    ...(o.id && { id: o.id }),
  }));

export const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const shouldSkipRendering = (key, pub_talent_type, userType) => {
  const isNotAthlete = pub_talent_type !== TalentType.ATHLETE;
  const isBrandMarketerOrAgencyOrSchool = [BRAND, AGENCY, SCHOOL].includes(userType);
  const isBrandMarketerOrAgency = [BRAND].includes(userType);
  const athleteKeys = [SCHOOL_OR_TEAM, SPORT, POSITION];
  const brandKeys = [ETHNICITY, HOMETOWN, SHIRT_SIZE, REFERRED_BY, BIO];
  const isJobTitle = [FAN, TALENT, SCHOOL, AGENCY].includes(userType);
  const isSchool = userType === SCHOOL;
  const isTalent = userType === TALENT;
  const locationRelatedKeys = [HOMETOWN, CURRENT_TOWN];
  // School name should be rendered only for schools
  if (key === SCHOOL_NAME && !isSchool) {
    return true;
  }
  if (key === SCHOOL_OR_GROUP_NAME && !isSchool) {
    return true;
  }
  if ([CURRENT_TOWN, HEIGHT, WEIGHT].includes(key) && !isTalent) {
    return true;
  }
  return (
    athleteKeys.includes(key) ||
    (isNotAthlete && key === POSITION) ||
    (isBrandMarketerOrAgencyOrSchool && brandKeys.includes(key)) ||
    (!isBrandMarketerOrAgency && key === COMPANY) ||
    (isJobTitle && key === JOB_TITLE) ||
    locationRelatedKeys.includes(key)
  );
};

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, values, currentStep } = props;
  const { pub_talent_type, pub_talent_level, userType } = values || {};
  const { enumOptions = [], saveConfig, key } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });

  const filterOptions = createFilterOptions(enumOptions);
  const label = getLabel(fieldConfig);
  const isBrandMarketerOrAgencyOrSchool = [BRAND, AGENCY, SCHOOL].includes(userType);
  const brandKeys = [ETHNICITY, SHIRT_SIZE];
  const skipConditions = {
    [ATHLETE]: pub_talent_type !== TalentType.ATHLETE,
    [TALENT_TYPE]: currentStep !== INITIAL,
    [CONFERENCE]:
      toLowerCase(pub_talent_level) !== toLowerCase(TalentLevel.COLLEGIATE) ||
      toLowerCase(pub_talent_type) !== toLowerCase(TalentType.ATHLETE) ||
      currentStep !== FINAL,
    [TALENT_LEVEL]:
      (currentStep !== INITIAL &&
        toLowerCase(pub_talent_type) !== toLowerCase(TalentType.ATHLETE) &&
        toLowerCase(pub_talent_type) !== toLowerCase(TalentType.COACH)) ||
      currentStep === FINAL,
    [GENDER]:
      (userType === TALENT && currentStep !== FINAL) || [BRAND, AGENCY, SCHOOL].includes(userType),
    [AGENCY_FIELD]: userType === TALENT || [BRAND, AGENCY, SCHOOL].includes(userType),
    [ETHNICITY]: isBrandMarketerOrAgencyOrSchool,
    [SHIRT_SIZE]: isBrandMarketerOrAgencyOrSchool,
    [STATE_OR_TERRITORY]: userType !== TALENT || currentStep !== FINAL,
    [USER_TYPE_SCT]: userType !== SCHOOL,
  };

  const shouldSkipRendering = () => {
    if (key in skipConditions && skipConditions[key]) {
      return true;
    }
    if (userType === TALENT && currentStep !== FINAL && brandKeys.includes(key)) {
      return true;
    }
    return false;
  };

  if (shouldSkipRendering()) {
    return null;
  }

  if (key === PRIMARY_INDUSTRY) {
    return (
      <RenderBrandIndustries
        name={name}
        formId={formId}
        label={label}
        validateMaybe={validateMaybe}
        placeholder={placeholder}
        filterOptions={filterOptions}
      />
    );
  }

  if ([TALENT_TYPE, TALENT_LEVEL].includes(key)) {
    return (
      <RenderTalentTypes
        name={name}
        formId={formId}
        label={label}
        validateMaybe={validateMaybe}
        placeholder={placeholder}
        filterOptions={filterOptions}
        dependentValue={pub_talent_type}
      />
    );
  }

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={
        [`pub_${USER_TYPE_SCT}`].includes(name) ? (
          <>
            {label} <InfoPopup info="This Group Option is for Schools, Collectives and Teams." />
          </>
        ) : (
          label
        )
      }
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {sortOptionsByKeyIntl(filterOptions, 'label', name).map(({ value, label }) => (
        <option key={`${value}_${label}`} value={value}>
          {label}
        </option>
      ))}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    form,
    formId,
    values,
    currentStep,
    viewport,
  } = props;
  const { userType, pub_talent_type } = values || {};
  const { enumOptions = [], saveConfig, key } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};

  const shouldSkipRenderingConditions = {
    [INTEREST]: toLowerCase(userType) !== TALENT || currentStep !== FINAL,
    [SPORT]: toLowerCase(pub_talent_type) !== TalentType.ATHLETE || currentStep !== FINAL,
  };

  const shouldSkipRendering =
    key in shouldSkipRenderingConditions && shouldSkipRenderingConditions[key];

  if (shouldSkipRendering) {
    return null;
  }

  const FieldComponent = fieldComponents[name];

  return FieldComponent ? (
    <FieldComponent
      name={name}
      formId={formId}
      label={label}
      form={form}
      values={values}
      enumOptions={enumOptions}
      viewport={viewport}
    />
  ) : enumOptions ? (
    <div className={classNames(css.customField, css.customSelectField)}>
      <Field
        component={FieldMultiSelect}
        isMulti={true}
        id={formId ? `${formId}.${name}` : name}
        name={name}
        label={label}
        options={createMultiFilterOptions(enumOptions)}
        {...validateMaybe}
      />
    </div>
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, values, currentStep } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const key = fieldConfig.key || {};
  const { pub_talent_type, userType } = values || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  // Skip rendering logic
  if (shouldSkipRendering(key, pub_talent_type, userType)) {
    return null;
  }

  if (userType === TALENT && currentStep !== FINAL) {
    return true; // Skip rendering for talent users unless at the final step
  }

  if (userType === BRAND && name === FieldKeys.COMPANY) {
    return true; // Skip rendering for talent users unless at the final step
  }

  const isHeightField = key === 'height';
  const isHometownField = key === 'hometown';
  // Handle hometown and current location
  if (isHometownField) {
    return (
      <div className={css.hometownFields}>
        {/* Hometown */}
        <div className={css.locationRow}>
          <FieldTextInput
            className={css.locationInput}
            id={formId ? `${formId}.${name}.hometown` : `${name}.hometown`}
            name={`${name}.hometown`}
            type="text"
            label="Home Town"
            placeholder="e.g., Los Angeles"
            validate={requiredMessage ? required(requiredMessage) : undefined}
          />
          <FieldSelect
            className={css.stateSelect}
            id={formId ? `${formId}.${name}.hometownState` : `${name}.hometownState`}
            name={`${name}.hometownState`}
            label="State"
            validate={requiredMessage ? required(requiredMessage) : undefined}
          >
            <option disabled value="">
              Select a state
            </option>
            {US_STATES.map(state => (
              <option key={state.value} value={state.value}>
                {state.value}
              </option>
            ))}
          </FieldSelect>
        </div>
      </div>
    );
  }

  // Handle height inputs
  if (isHeightField) {
    return (
      <div className={css.heightFields}>
        <FieldTextInput
          className={css.heightInput}
          id={formId ? `${formId}.${name}.feet` : `${name}.feet`}
          name={`${name}.feet`}
          type="number"
          label={`${label} (Feet)`}
          placeholder="e.g., 6"
          validate={requiredMessage ? required(requiredMessage) : undefined}
        />
        <FieldTextInput
          className={css.heightInput}
          id={formId ? `${formId}.${name}.inches` : `${name}.inches`}
          name={`${name}.inches`}
          type="number"
          label={`${label} (Inches)`}
          placeholder="e.g., 4"
          validate={requiredMessage ? required(requiredMessage) : undefined}
        />
      </div>
    );
  }
  if (key === DATE_OF_BIRTH && currentStep === FINAL) {
    return (
      <div className={classNames(css.customField, css.customSelectField)}>
        <DateOfBirthField
          name="dateOfBirth"
          label={intl.formatMessage({ id: 'TalentFormCard.dateOfBirth.label' })}
          validate={required(intl.formatMessage({ id: 'TalentFormCard.dateOfBirth.required' }))}
          placeholder={intl.formatMessage({
            id: 'TalentFormCard.dateOfBirth.placeholder',
          })}
        />
      </div>
    );
  }

  return (
    <FieldTextInput
      className={classNames(css.customField, key === BIO ? css.customTextareaField : null)}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type={key === BIO ? 'textarea' : 'text'}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, values, currentStep } = props;
  const { minimum, maximum, saveConfig, key } = fieldConfig;
  const { userType } = values || {};

  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );
  const isTalent = userType === TALENT;
  const isFinalStep = currentStep === FINAL;

  // Only show the field if the user is a talent and the current step is final
  if (!isTalent || !isFinalStep) {
    return null; // Return null to render nothing
  }
  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };
  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, props)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, props)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, props)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, props)
    : null;
};

export default CustomExtendedDataField;
